'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'

import HolidaysSpotlight from './holidaysSpotlight'

/** One prompt per browsing session, whichever surface shows it first. */
const SESSION_KEY = 'holidaysSpotlightShown'

/** Long enough to read twice, short enough not to sit over the page. */
const VISIBLE_MS = 8000

interface HolidaysSpotlightWrapperProps {
    /** `up` points the caret at a host below the bubble; `down` at one above it. */
    caret: 'up' | 'down'
}

const HolidaysSpotlightWrapper: React.FC<HolidaysSpotlightWrapperProps> = ({ caret }) => {
    const t = useTranslations('holidaysPage')
    const commonT = useTranslations('common')
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        // Starts hidden and is raised here: the server cannot know what this browser has seen.
        let alreadyShown = false
        try {
            alreadyShown = window.sessionStorage.getItem(SESSION_KEY) === 'r'
        } catch {
            alreadyShown = false
        }
        if (alreadyShown) return undefined

        try {
            window.sessionStorage.setItem(SESSION_KEY, '1')
        } catch {
            // A session that cannot be marked prompts again next page. Better than never.
        }
        setVisible(true)

        const timer = window.setTimeout(() => setVisible(false), VISIBLE_MS)
        return () => window.clearTimeout(timer)
    }, [])

    const handleClose = useCallback(() => setVisible(false), [])

    if (!visible) return null

    return (
        <HolidaysSpotlight
            message={t('spotlightMessage')}
            closeLabel={commonT('close')}
            onClose={handleClose}
            caret={caret}
        />
    )
}

export { HolidaysSpotlightWrapper }
export default HolidaysSpotlightWrapper
